import React from 'react';
import {
  X, ClipboardList, Send, CheckCircle, PackageCheck, XCircle,
  Calendar, User, Building2, FileText, Clock, Edit2,
} from 'lucide-react';
import { P } from '@/config/permissionCodes';
import { useAuth } from '../../../../context/AuthContext';

const STATUS_BADGE = {
  draft:              'bg-slate-100 text-slate-600',
  pending:            'bg-amber-100 text-amber-700',
  approved:           'bg-blue-100 text-blue-700',
  received:           'bg-green-100 text-green-700',
  partially_received: 'bg-violet-100 text-violet-700',
  cancelled:          'bg-red-100 text-red-700',
};

const STATUS_LABEL = {
  draft:              'Draft',
  pending:            'Pending',
  approved:           'Approved',
  received:           'Received',
  partially_received: 'Partially Received',
  cancelled:          'Cancelled',
};

const TIMELINE_STEPS = [
  { status: 'draft',    label: 'Created',   color: 'bg-slate-400' },
  { status: 'pending',  label: 'Submitted', color: 'bg-amber-400' },
  { status: 'approved', label: 'Approved',  color: 'bg-blue-400'  },
  { status: 'received', label: 'Received',  color: 'bg-green-500' },
];

const STATUS_ORDER = ['draft', 'pending', 'approved', 'partially_received', 'received'];

const formatDate = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleDateString('en-IN'); }
  catch { return value; }
};

const resolveTotal    = (po) => po.totalAmount ?? po.grandTotal ?? 0;
const resolveSubtotal = (po) => po.subtotal    ?? po.subTotal  ?? 0;
const resolveTax      = (po) => po.taxAmount   ?? po.tax       ?? 0;
const resolvedReceived = (li) => li.receivedQuantity ?? li.receivedQty ?? li.received ?? 0;

// Currency with 2 decimals (e.g. ₹654.50)
const money = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// FIX 1: Added `onEdit` to the destructured props
export default function PurchaseOrderDetail({ po, onClose, onEdit, onSubmit, onApprove, onReceive, onCancel }) {
  const { hasPermission } = useAuth();
  const canEdit   = hasPermission(P.INVENTORY.PURCHASE_ORDERS.EDIT);
  const canDelete = hasPermission(P.INVENTORY.PURCHASE_ORDERS.DELETE);

  if (!po) return null;

  const currentIdx  = STATUS_ORDER.indexOf(po.status);
  const isCancelled = po.status === 'cancelled';

  // New optional fields (safe for older POs that don't have them)
  const discountAmount = Number(po.discountAmount) || 0;
  const extraCharges   = Number(po.extraCharges) || 0;
  const extraChargesDetails = Array.isArray(po.extraChargesDetails) ? po.extraChargesDetails : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            <div>
              <h2 className="text-base font-bold text-slate-800">{po.poNumber}</h2>
              <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[po.status] || 'bg-slate-100 text-slate-600'}`}>
                {STATUS_LABEL[po.status] || po.status}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <InfoField icon={Building2} label="Supplier"          value={po.supplierName || '—'} />
            <InfoField icon={Calendar}  label="Order Date"        value={po.orderDate || '—'} />
            <InfoField icon={Calendar}  label="Expected Delivery" value={po.expectedDeliveryDate || '—'} />
            <InfoField icon={User}      label="Created By"        value={po.createdBy || po.createdByName || 'Admin'} />
            <InfoField icon={Clock}     label="Last Updated"      value={formatDate(po.updatedAt)} />
          </div>

          {/* Notes */}
          {po.notes && (
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Notes</span>
              </div>
              <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">{po.notes}</p>
            </div>
          )}

          {/* Line Items Table */}
          <div>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Line Items</h3>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs min-w-[680px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left   px-3 py-2 font-semibold text-slate-600">Item</th>
                    <th className="text-center px-3 py-2 font-semibold text-slate-600">Qty</th>
                    <th className="text-center px-3 py-2 font-semibold text-slate-600">UOM</th>
                    <th className="text-right  px-3 py-2 font-semibold text-slate-600">Unit Price</th>
                    <th className="text-right  px-3 py-2 font-semibold text-slate-600">Amount</th>
                    <th className="text-right  px-3 py-2 font-semibold text-slate-600">Tax</th>
                    <th className="text-right  px-3 py-2 font-semibold text-slate-600">Total</th>
                    <th className="text-center px-3 py-2 font-semibold text-slate-600">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(po.lineItems ?? po.items ?? []).map((li, idx) => {
                    const received  = resolvedReceived(li);
                    const amount    = Number(li.totalPrice ?? li.total ?? (li.quantity * li.unitPrice)) || 0;
                    const taxAmount = Number(li.taxAmount) || 0;
                    const lineTotal = amount + taxAmount; // Total incl. tax — computed in the UI
                    return (
                      <tr key={li.id ?? idx} className="hover:bg-slate-50/40">
                        <td className="px-3 py-2 text-slate-700 font-medium">{li.itemName ?? li.name}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{li.quantity}</td>
                        <td className="px-3 py-2 text-center text-slate-500">{li.uom ?? li.unit ?? '—'}</td>
                        <td className="px-3 py-2 text-right text-slate-600">₹{(li.unitPrice ?? 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right text-slate-600">₹{amount.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right text-slate-600">₹{taxAmount.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right font-medium text-slate-700">₹{lineTotal.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`${
                            received >= li.quantity ? 'text-green-600 font-semibold'
                            : received > 0          ? 'text-amber-600'
                                                    : 'text-slate-400'
                          }`}>
                            {received} / {li.quantity}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Other Charges Breakdown */}
          {extraChargesDetails.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Other Charges</h3>
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs min-w-[480px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left  px-3 py-2 font-semibold text-slate-600">Description</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Amount</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Tax</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {extraChargesDetails.map((c, idx) => {
                      const amt = Number(c.amount) || 0;
                      const tax = Number(c.taxAmount) || 0;
                      const tot = c.totalAmount != null ? Number(c.totalAmount) : amt + tax;
                      return (
                        <tr key={idx} className="hover:bg-slate-50/40">
                          <td className="px-3 py-2 text-slate-700">{c.description ?? c.key ?? c.name ?? '—'}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{money(amt)}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{money(tax)}</td>
                          <td className="px-3 py-2 text-right font-medium text-slate-700">{money(tot)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div>
            <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3.5 space-y-2.5">
                {/* Subtotal */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-medium text-slate-700 tabular-nums">{money(resolveSubtotal(po))}</span>
                </div>

                {/* Item Tax */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Item Tax</span>
                  <span className="font-medium text-slate-700 tabular-nums">{money(resolveTax(po))}</span>
                </div>

                {/* Discount */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Discount</span>
                  <span className="font-medium text-emerald-600 tabular-nums">
                    {discountAmount > 0 ? `− ${money(discountAmount)}` : money(0)}
                  </span>
                </div>

                {/* Other Charges (incl. tax) */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Other Charges (Incl. Tax)</span>
                  <span className="font-medium text-slate-700 tabular-nums">{money(extraCharges)}</span>
                </div>
              </div>

              {/* Grand Total */}
              <div className="flex items-center justify-between px-4 py-3 bg-teal-50 border-t border-teal-100">
                <span className="text-sm font-semibold text-teal-800">Grand Total</span>
                <span className="text-base font-bold text-teal-700 tabular-nums">{money(resolveTotal(po))}</span>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          <div>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Status Timeline</h3>
            <div className="space-y-0">
              {TIMELINE_STEPS.map((step, idx) => {
                const stepIdx   = STATUS_ORDER.indexOf(step.status);
                const isActive  = !isCancelled && stepIdx <= currentIdx;
                const isCurrent = !isCancelled && step.status === po.status;
                return (
                  <div key={step.status} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full border-2 ${
                        isActive ? `${step.color} border-transparent` : 'bg-white border-slate-300'
                      } ${isCurrent ? 'ring-2 ring-offset-1 ring-teal-300' : ''}`} />
                      {idx < TIMELINE_STEPS.length - 1 && (
                        <div className={`w-0.5 h-6 ${isActive && stepIdx < currentIdx ? 'bg-slate-300' : 'bg-slate-200'}`} />
                      )}
                    </div>
                    <span className={`text-xs -mt-0.5 ${isActive ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
              {isCancelled && (
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-400 ring-2 ring-offset-1 ring-red-200" />
                  <span className="text-xs -mt-0.5 text-red-600 font-medium">Cancelled</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
          >
            Close
          </button>

          {/* FIX 2: Edit2 is now imported; onEdit is now a received prop */}
          {canEdit && po.status === 'draft' && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-700 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          )}

          {canEdit && po.status === 'draft' && (
            <button
              onClick={onSubmit}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Send className="w-3.5 h-3.5" /> Submit
            </button>
          )}

          {canEdit && po.status === 'pending' && (
            <button
              onClick={onApprove}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-3.5 h-3.5" /> Approve
            </button>
          )}

          {canEdit && (po.status === 'approved' || po.status === 'partially_received') && (
            <button
              onClick={onReceive}
              className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 transition-colors"
            >
              <PackageCheck className="w-3.5 h-3.5" /> Receive Items
            </button>
          )}

          {canEdit && !['cancelled', 'received'].includes(po.status) && (
            <button
              onClick={onCancel}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              <XCircle className="w-3.5 h-3.5" /> Cancel PO
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3 text-slate-400" />
        <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</span>
      </div>
      <p className="text-xs text-slate-700 font-medium">{value}</p>
    </div>
  );
}